/* eslint-disable prettier/prettier */
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from '../../../../styles/components/pageComponents/program/ProgramContentBuilder.module.css';
import { ModuleType, ActivityType } from '../../../../types/content';
import { ActivityItem } from './ActivityItem';
import { DateRangePicker } from '../../../controlVariations/DateRangePicker';

interface ModuleItemProps {
	moduleData: ModuleType;
	index: number;
	canAddActivity: boolean;
	onUpdate: (id: string, field: keyof ModuleType, value: string) => void;
	onDelete: (id: string) => void;
	onAddActivity: (moduleId: string) => void;
	onUpdateActivity: (
		moduleId: string,
		activityId: string,
		field: keyof ActivityType,
		value: string
	) => void;
	onDeleteActivity: (moduleId: string, activityId: string) => void;

    setActivitiesWithTimeErrors: React.Dispatch<
        React.SetStateAction<Set<string>>
    >;

}



export const ModuleItem: React.FC<ModuleItemProps> = ({
	moduleData,
	index,
	canAddActivity,
	onUpdate,
	onDelete,
	onAddActivity,
	onUpdateActivity,
	onDeleteActivity,
	setActivitiesWithTimeErrors
}) => {
	const [isExpanded, setIsExpanded] = useState(true);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const [showActivities, setShowActivities] = useState(
    moduleData.activities.some(activity => !activity.isDeleted)
);

    useEffect(() => {
    const hasActivities = moduleData.activities.some(
        activity => !activity.isDeleted
    );

    if (hasActivities) {
        setShowActivities(true);
    }
}, [moduleData.activities]);
	const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });

	const [tempRange, setTempRange] = useState<{
		start: Date | null;
		end: Date | null;
	}>({
		start: null,
		end: null,
	});

	const triggerRef = useRef<HTMLDivElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const PICKER_HEIGHT = 420;
	const PICKER_WIDTH = 340;
	const GAP = 8;

	const updatePos = React.useCallback(() => {
		if (!triggerRef.current) return;

		const rect = triggerRef.current.getBoundingClientRect();

		const pickerWidth = calendarRef.current?.offsetWidth ?? 340;
		const pickerHeight = calendarRef.current?.offsetHeight ?? 420;

		const spaceBelow = window.innerHeight - rect.bottom;
		const spaceRight = window.innerWidth - rect.left;

		let top =
			spaceBelow >= pickerHeight
				? rect.bottom + GAP
				: rect.top - pickerHeight - GAP;

		let left =
			spaceRight >= pickerWidth
				? rect.left
				: rect.right - pickerWidth;

		top = Math.max(12, top);

		left = Math.max(
			12,
			Math.min(left, window.innerWidth - pickerWidth - 12)
		);

		setCalendarPos({ top, left });
	}, []);

	// Close on outside click
	useEffect(() => {
		if (!isCalendarOpen) return;

		updatePos();

		window.addEventListener('scroll', updatePos, true);
		window.addEventListener('resize', updatePos);

		return () => {
			window.removeEventListener('scroll', updatePos, true);
			window.removeEventListener('resize', updatePos);
		};
	}, [isCalendarOpen, updatePos]);

	useEffect(() => {
		if (!isCalendarOpen) return;

		requestAnimationFrame(() => {
			updatePos();
		});
	}, [isCalendarOpen, updatePos]);
	// Recompute position on scroll/resize so portal stays anchored
	useEffect(() => {
		if (!isCalendarOpen) return;
		const updatePos = () => {
			if (!triggerRef.current) return;

			const rect = triggerRef.current.getBoundingClientRect();

			const pickerWidth =
				calendarRef.current?.offsetWidth ?? 340;

			const pickerHeight =
				calendarRef.current?.offsetHeight ?? 420;

			const spaceBelow = window.innerHeight - rect.bottom;
			const spaceRight = window.innerWidth - rect.left;

			let top =
				spaceBelow >= pickerHeight
					? rect.bottom + GAP
					: rect.top - pickerHeight - GAP;

			let left =
				spaceRight >= pickerWidth
					? rect.left
					: rect.right - pickerWidth;

			top = Math.max(12, top);

			left = Math.max(
				12,
				Math.min(left, window.innerWidth - pickerWidth - 12)
			);

			setCalendarPos({ top, left });
		};


		updatePos();
		const handleScroll = () => {

			setTempRange({
				start: null,
				end: null,
			});

			setIsCalendarOpen(false);
		};


		window.addEventListener('scroll', handleScroll, true);

		return () => {
			window.removeEventListener('scroll', handleScroll, true);
			window.removeEventListener('resize', updatePos);
		};
	}, [isCalendarOpen]);

	useEffect(() => {
	if (!isCalendarOpen) return;

	const handleClickOutside = (e: MouseEvent) => {
		if (
			calendarRef.current &&
			!calendarRef.current.contains(e.target as Node) &&
			triggerRef.current &&
			!triggerRef.current.contains(e.target as Node)
		) {
			setIsCalendarOpen(false);
		}
	};

	document.addEventListener('mousedown', handleClickOutside);

	return () => {
		document.removeEventListener('mousedown', handleClickOutside);
	};
}, [isCalendarOpen]);

	const handleCalendarToggle = () => {
		if (!isCalendarOpen && triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();

			setCalendarPos({
				top: rect.bottom + GAP,
				left: rect.left,
			});
		}
		setIsCalendarOpen(prev => !prev);
	};

	const formatDate = (date: Date | string | null) => {
		if (!date) return '';
		const parsed = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(parsed.getTime())) return '';
		return parsed.toLocaleDateString('en-GB', {
			day: '2-digit',
			month: 'short',
			year: '2-digit',
		});
	};

	const formatLocalDate = (date: Date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	};

	const handleRangeSelect = (range: {
		start: Date | null;
		end: Date | null;
	}) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (range.start && range.start < today) return;
		if (range.end && range.end < today) return;

		// Store selection locally
		setTempRange(range);

		// Only commit when both dates are selected
		if (range.start && range.end) {
			onUpdate(
				moduleData.id,
				'startdate',
				formatLocalDate(range.start)
			);

			onUpdate(
				moduleData.id,
				'enddate',
				formatLocalDate(range.end)
			);

			setTempRange({
				start: null,
				end: null,
			});

			setIsCalendarOpen(false);
		}
	};
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		field: keyof ModuleType
	) => {
		onUpdate(moduleData.id, field, e.target.value);
	};

	return (
		<div className={styles.card}>
			<div className={styles.cardHeader}>
				<h3 className={styles.cardTitle}>Module {index + 1}</h3>
				<div className={styles.cardControls}>
					<button
						className={styles.iconBtn}
						onClick={() => onDelete(moduleData.id)}
						title="Delete Module"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="22"
							viewBox="0 0 20 22"
							fill="none"
						>
							<path
								d="M7.75 9.75V15.75M11.75 9.75V15.75M16.75 4.75V18.75C16.75 19.2804 16.5393 19.7891 16.1642 20.1642C15.7891 20.5393 15.2804 20.75 14.75 20.75H4.75C4.21957 20.75 3.71086 20.5393 3.33579 20.1642C2.96071 19.7891 2.75 19.2804 2.75 18.75V4.75M0.75 4.75H18.75M5.75 4.75V2.75C5.75 2.21957 5.96071 1.71086 6.33579 1.33579C6.71086 0.960714 7.21957 0.75 7.75 0.75H11.75C12.2804 0.75 12.7891 0.960714 13.1642 1.33579C13.5393 1.71086 13.75 2.21957 13.75 2.75V4.75"
								stroke="#1E1E1E"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						className={styles.iconBtn}
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded ? (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="8"
								viewBox="0 0 14 8"
								fill="none"
							>
								<path
									d="M13 7L7 1L1 7"
									stroke="#949494"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="8"
								viewBox="0 0 14 8"
								fill="none"
							>
								<path
									d="M1 1L7 7L13 1"
									stroke="#949494"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						)}
					</button>
				</div>
			</div>

			{isExpanded && (
				<div className={styles.cardBody}>
					<div className={styles.formRow}>
						<div className={styles.formGroup} style={{ flex: 2 }}>
							<label className={styles.label}>
								Module Name
								<span className={styles.required}>*</span>
							</label>
							<input
								type="text"
								className={styles.inputControl}
								placeholder="IMPACT PART 1"
								value={moduleData.name}
								onChange={e => handleChange(e, 'name')}
							/>
							{moduleData.errors?.name && (
								<span className={styles.error}>
									{moduleData.errors.name}
								</span>
							)}
						</div>

						{/* Trigger container — used for measuring position only */}
						<div className={styles.formGroup} style={{ flex: 1 }}>
							<label className={styles.label}>
								Date Range
								<span className={styles.required}>*</span>
							</label>
							<div className={styles.inputGroup} ref={triggerRef}>
								<span
									className={styles.iconWrapper}
									onClick={handleCalendarToggle}
									style={{ cursor: 'pointer' }}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="20"
										height="22"
										viewBox="0 0 20 22"
										fill="none"
									>
										<path
											d="M5.75 0.75V4.75M13.75 0.75V4.75M0.75 8.75H18.75M2.75 2.75H16.75C17.8546 2.75 18.75 3.64543 18.75 4.75V18.75C18.75 19.8546 17.8546 20.75 16.75 20.75H2.75C1.64543 20.75 0.75 19.8546 0.75 18.75V4.75C0.75 3.64543 1.64543 2.75 2.75 2.75Z"
											stroke="#1E1E1E"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</span>
								<input
									type="text"
									readOnly
									placeholder={'Start Date'}
									className={styles.inputControl}
									value={
										moduleData.startdate
											? formatDate(moduleData.startdate)
											: ''
									}
									onChange={e => handleChange(e, 'startdate')}
								/>

								{moduleData.errors?.startdate && (
									<span className={styles.error}>
										{moduleData.errors.startdate}
									</span>
								)}
								<span className={styles.separator}>to</span>
								<input
									type="text"
									readOnly
									placeholder={'End Date'}
									className={styles.inputControl}
									value={
										moduleData.enddate
											? formatDate(moduleData.enddate)
											: ''
									}
									onChange={e => handleChange(e, 'enddate')}
								/>

								{moduleData.errors?.enddate && (
									<span className={styles.error}>
										{moduleData.errors.enddate}
									</span>
								)}
							</div>
						</div>
					</div>

					<div className={styles.formGroup}>
						<label className={styles.label}>Description</label>
						<textarea
							className={`${styles.inputControl} ${styles.textarea}`}
							placeholder="Enter details (Optional)"
							maxLength={500}
							value={moduleData.description}
							onChange={e => handleChange(e, 'description')}
						/>
						<span className={styles.charCount}>
							{moduleData.description.length}/500
						</span>
					</div>
                    {/* {showActivities && (
					<div className={styles.activitiesContainer}>
						{moduleData.activities
							.filter(activity => !activity.isDeleted)
							.map((activity, actIndex) => (
								<ActivityItem
									key={activity.id}
									activity={activity}
									index={actIndex}
									onUpdate={(actId, field, val) =>
										onUpdateActivity(
											moduleData.id,
											actId,
											field,
											val
										)
									}
									onDelete={actId =>
										onDeleteActivity(moduleData.id, actId)
									}
								/>
							))}

						<div>
							

							<button
								className={`${styles.btn} ${styles.btnAddText}`}
								onClick={() => onAddActivity(moduleData.id)}
								disabled={!canAddActivity}
								style={{
									opacity: !canAddActivity ? 0.5 : 1,
									cursor: !canAddActivity ? 'not-allowed' : 'pointer',
								}}
							>
								Add Activity <span>+</span>
							</button>
						</div>
					</div>)} */}
					{/* Existing activities */}
{showActivities && (
    <div className={styles.activitiesContainer}>
        {moduleData.activities
            .filter(activity => !activity.isDeleted)
            .map((activity, actIndex) => (
                <ActivityItem
                    key={activity.id}
                    activity={activity}
                    index={actIndex}
                    onUpdate={(actId, field, val) =>
                        onUpdateActivity(
                            moduleData.id,
                            actId,
                            field,
                            val
                        )
                    }
                    onDelete={actId =>
                        onDeleteActivity(moduleData.id, actId)
                    }
                />
            ))}
    </div>
)}

{/* Add Activity button */}
{canAddActivity && (
    <div>
        <button
            className={`${styles.btn} ${styles.btnAddText}`}
            onClick={() => {
                onAddActivity(moduleData.id);
                setShowActivities(true);
            }}
        >
            Add Activity <span>+</span>
        </button>
    </div>
)}
				</div>
			)}

			{/* Portal: renders at document.body — completely escapes overflow:hidden on .card and .scrollArea */}
			{isCalendarOpen &&
				ReactDOM.createPortal(
					<div
						ref={calendarRef}
						style={{
							position: 'fixed',
							top: calendarPos.top,
							left: calendarPos.left,
							zIndex: 9999,
							background: '#fff',
							borderRadius: '12px',
							boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
						}}
					>
						<DateRangePicker
							key={isCalendarOpen ? 'open' : 'closed'}
							initialMonth={new Date()}
							onRangeSelect={handleRangeSelect}
							minDate={new Date()}
						/>
					</div>,
					document.body
				)}
		</div>
	);
};
